import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { html, nothing, TemplateResult } from 'lit';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { getSearchLabel } from '../utils/tabbed-search.utils';
import { StoreState } from '../../../store/store.interface';
import { BUSINESS_UNITS, TAB_NAMES } from '../constants';
import { getSearchV2ApiCall } from '../../search-v2/api/search-v2.apis';
import { getSearchApiCall } from '../../search/api/search.api';

@customElement('mhecomm-tabbed-suggestion-search-result')
export class TabbedSuggestionSearchResult extends connect(store)(BaseComponent) {
  @property() 
  searchTerm = '';

  // Add properties to track active tab from store
  @state()
  private activeTabKey = '';

  @state()
  private allTabs: Record<string, string> = {};

  @state()
  private searchProductResultsTotalCount = 0;

  @state()
  private searchArticleResultsTotalCount = 0;

  @state()
  private searchV2ResultsTotalCount = 0;

  @state()
  private searchDataLoaded = false;

  /**
     * Component connected to DOM - load initial state
     */
  async connectedCallback() {
    super.connectedCallback();

    // Get initial state when component loads
    const state = store.getState() as StoreState;
    const tabbedSearchState = state.tabbedSearchActiveTabState;

    if (tabbedSearchState) {
      this.activeTabKey = tabbedSearchState.activeTabKey;
      this.allTabs = tabbedSearchState.allTabs || {};
    }

    // Initialize data immediately after loading component
    await this.initializeData();
  }

  /**
   * Component disconnected from DOM - cleanup
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    // Perform any necessary cleanup here
  }

  /**
   * Main render function
   */
  renderComp() {
    return html`
      <div role="search" aria-label="${getSearchLabel(this.activeTabKey)}">
        <div>
          <p class="fs-large" aria-live="polite" aria-atomic="true">
            ${this.searchDataLoaded 
              ? this.renderSuggestionMessage() 
              : html`<span class="loading-text" role="status" aria-busy="true">Looking for suggestions...</span>`}
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Initialize data fetching
   */
  private async initializeData() {
    if (!this.searchDataLoaded && this.searchTerm) {
      try {
        switch(this.activeTabKey) {
          case TAB_NAMES.PRODUCT:
            [this.searchArticleResultsTotalCount, this.searchV2ResultsTotalCount] = await Promise.all([
              this.fetchArticleResults(),
              this.fetchSupportResults()
            ]);
            break;
          case TAB_NAMES.SUPPORT:
            [this.searchProductResultsTotalCount, this.searchArticleResultsTotalCount] = await Promise.all([
              this.fetchProductResults(),
              this.fetchArticleResults()
            ]);
            break;
          case TAB_NAMES.ARTICLES:
            [this.searchProductResultsTotalCount, this.searchV2ResultsTotalCount] = await Promise.all([
              this.fetchProductResults(),
              this.fetchSupportResults()
            ]);
            break;
          default:
            [this.searchArticleResultsTotalCount, this.searchV2ResultsTotalCount] = await Promise.all([
              this.fetchArticleResults(),
              this.fetchSupportResults()
            ]);
        }
      } catch (error) {
        console.error('Error fetching search data:', error);
      } finally {
        this.searchDataLoaded = true;
        this.requestUpdate();
      }
    }
  }

  /**
   * Renders the suggestion message after data is loaded
   */
  private renderSuggestionMessage(): TemplateResult | typeof nothing {
    const productTabName = this.allTabs[TAB_NAMES.PRODUCT];
    const supportTabName = this.allTabs[TAB_NAMES.SUPPORT];
    const articlesTabName = this.allTabs[TAB_NAMES.ARTICLES];
    
    switch(this.activeTabKey) {
      case TAB_NAMES.PRODUCT:
        return this.getProductTabSuggestion(supportTabName, articlesTabName);
      case TAB_NAMES.SUPPORT:
        return this.getSupportTabSuggestion(productTabName, articlesTabName);
      case TAB_NAMES.ARTICLES:
        return this.getArticlesTabSuggestion(productTabName, supportTabName);
      default:
        return this.getProductTabSuggestion(supportTabName, articlesTabName);
    }
  }
  
  /**
   * Generates suggestions when on the Product tab
   */
  private getProductTabSuggestion(supportTabName: string, articlesTabName: string): TemplateResult | typeof nothing {
    const hasArticlesSearchResults = this.searchArticleResultsTotalCount !== 0;
    const hasSearchV2Results = this.searchV2ResultsTotalCount !== 0;
    
    if (hasSearchV2Results && hasArticlesSearchResults) {
      return this.getCombinedSuggestion(supportTabName, this.searchV2ResultsTotalCount, articlesTabName, this.searchArticleResultsTotalCount);
    } else if (hasSearchV2Results) {
      return this.getIndividualSuggestion(supportTabName, this.searchV2ResultsTotalCount);
    } else if (hasArticlesSearchResults) {
      return this.getIndividualSuggestion(articlesTabName, this.searchArticleResultsTotalCount);
    }
    return nothing;
  }
  
  /**
   * Generates suggestions when on the Support tab
   */
  private getSupportTabSuggestion(productTabName: string, articlesTabName: string): TemplateResult | typeof nothing {
    const hasProductSearchResults = this.searchProductResultsTotalCount !== 0;
    const hasArticlesSearchResults = this.searchArticleResultsTotalCount !== 0;
    
    if (hasProductSearchResults && hasArticlesSearchResults) {
      return this.getCombinedSuggestion(productTabName, this.searchProductResultsTotalCount, articlesTabName, this.searchArticleResultsTotalCount);
    } else if (hasProductSearchResults) {
      return this.getIndividualSuggestion(productTabName, this.searchProductResultsTotalCount);
    } else if (hasArticlesSearchResults) {
      return this.getIndividualSuggestion(articlesTabName, this.searchArticleResultsTotalCount);
    }
    return nothing;
  }
  
  /**
   * Generates suggestions when on the Articles tab
   */
  private getArticlesTabSuggestion(productTabName: string, supportTabName: string): TemplateResult | typeof nothing {
    const hasProductSearchResults = this.searchProductResultsTotalCount !== 0;
    const hasSearchV2Results = this.searchV2ResultsTotalCount !== 0;
    
    if (hasSearchV2Results && hasProductSearchResults) {
      return this.getCombinedSuggestion(supportTabName, this.searchV2ResultsTotalCount, productTabName, this.searchProductResultsTotalCount);
    } else if (hasSearchV2Results) {
      return this.getIndividualSuggestion(supportTabName, this.searchV2ResultsTotalCount);
    } else if (hasProductSearchResults) {
      return this.getIndividualSuggestion(productTabName, this.searchProductResultsTotalCount);
    }
    return nothing;
  }
  
  /**
   * Generates a suggestion message for a single tab
   * @param tabName The name of the tab to suggest
   * @param resultCount The number of results in that tab
   * @returns Formatted suggestion message
   */
  private getIndividualSuggestion(tabName: string, resultCount: number): TemplateResult {
    return html`See the <b>${tabName}</b> tab for <b>${resultCount}</b> results.`;
  }
  
  /**
   * Generates a combined suggestion message for two tabs
   * @param firstTabName The name of the first tab to suggest
   * @param firstResultCount The number of results in the first tab
   * @param secondTabName The name of the second tab to suggest
   * @param secondResultCount The number of results in the second tab
   * @returns Formatted suggestion message
   */
  private getCombinedSuggestion(
    firstTabName: string, 
    firstResultCount: number, 
    secondTabName: string, 
    secondResultCount: number
  ): TemplateResult {
    return html`See the <b>${firstTabName}</b> tab for <b>${firstResultCount}</b> results, or the <b>${secondTabName}</b> tab for <b>${secondResultCount}</b> results.`;
  }

  private async fetchProductResults(): Promise<number> {
    return await this.fetchSearchResults(BUSINESS_UNITS.PRODUCTS);
  }

  private async fetchArticleResults(): Promise<number> { 
    return await this.fetchSearchResults(BUSINESS_UNITS.ARTICLES);
  }

  private async fetchSupportResults(): Promise<number> { 
    return await this.fetchSearchV2Results();
  }

  /**
   * Fetches article search results
   * @returns The total count of article results or 0 if there was an error
   */
  private async fetchSearchResults(catalog: string): Promise<number> {
    try {
      const results = await getSearchApiCall({
        fieldValueFilter: {},
        searchTerms: this.searchTerm,
        start: 1,
        catalog: catalog.toUpperCase()
      });
      return results?.total || 0;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.log(`Failed to get search results: ${errorMessage}`);
      return 0;
    }
  }

  /**
   * Fetches support search results
   * @returns The total count of support results or 0 if there was an error
   */
  private async fetchSearchV2Results(): Promise<number> {
    try {
      const results = await getSearchV2ApiCall({
        searchTerms: this.searchTerm,
        pageLength: 10,
        start: 1,
        facets: true,
        fieldValueFilter: {}
      });
      return results?.page?.totalDocuments || 0;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.log(`Failed to get search v2 results: ${errorMessage}`);
      return 0;
    }
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-tabbed-suggestion-search-result': TabbedSuggestionSearchResult;
  }
}
